import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getTelegramLinkStatus,
  startTelegramLink,
  getTelegramNotificationSettings,
  updateTelegramNotificationSettings,
  TelegramNotificationSettingsApiError,
} from "./telegramNotificationSettingsApi";

import {
  SPORTSBOOK_GROUPS,
  SELECTABLE_SPORTSBOOK_IDS,
  isSelectableSportsbookId,
} from "./sportsbookCatalog.shared";

import {
  TELEGRAM_BOOKMAKER_FILTER_MODES,
  normalizeTelegramBookmakerIds,
  buildTelegramBookmakerDraftFromSettings,
  validateTelegramBookmakerDraft,
  telegramBookmakerDraftEqualsSettings,
} from "./telegramBookmakerDraft.shared";

// PHASE 2C.2 — Telegram Notification Settings UI component.
//
// Renders ONLY the inner content of a detail card (no page title, no
// back button, no bottom nav, no full-screen wrapper). The parent
// AnalysisStatsPage.jsx is responsible for that chrome; this file is
// not wired into it yet.
//
// This module does not call any API at import/require time. All API
// calls happen from inside effects/handlers that only run after this
// component has mounted.

// ── Colour tokens (kept local/self-contained; not imported from
//    AnalysisStatsPage.jsx, only visually aligned with it) ──────────────────
const C = {
  bg: "#050816",
  card: "#0d1a38",
  cardBorder: "rgba(180,197,235,0.10)",
  accent: "#4c6fff",
  accentSoft: "rgba(76,111,255,0.14)",
  text: "#f4f7ff",
  muted: "#a8b3cf",
  dimmer: "#7d89a8",
  positive: "#00c878",
  positiveBg: "rgba(0,200,120,0.10)",
  positiveBorder: "rgba(0,200,120,0.22)",
  neutral: "#f2d878",
  neutralBg: "rgba(242,216,120,0.10)",
  neutralBorder: "rgba(242,216,120,0.22)",
  negative: "#ff6b4a",
  negativeBg: "rgba(255,107,74,0.10)",
  negativeBorder: "rgba(255,107,74,0.22)",
  inputBg: "#091022",
  inputBorder: "rgba(180,197,235,0.15)",
  divider: "rgba(180,197,235,0.08)",
};

// ── Stable default API client (module scope, built from the four real
//    named exports; never constructed lazily inside the component so
//    that the object identity stays stable across renders) ─────────────────
const DEFAULT_API_CLIENT = Object.freeze({
  getTelegramLinkStatus,
  startTelegramLink,
  getTelegramNotificationSettings,
  updateTelegramNotificationSettings,
});

// ── Tab contract ─────────────────────────────────────────────────────────────
const TABS = [
  { key: "steam", label: "Biến Động", icon: "📈" },
  { key: "value", label: "Giá Trị", icon: "💰" },
  { key: "arb", label: "Chênh Giá", icon: "⚖️" },
  { key: "middles", label: "Kèo Giữa", icon: "🎯" },
];

// Exact accept-list of tabs allowed to render a writable settings form
// and issue a PUT. Deliberately NOT derived from TABS (which also
// contains the read-only "steam" / "middles" placeholders). No
// trim/lowercase/String(...) coercion is ever applied to a tab key
// before checking membership here.
const WRITABLE_UI_TABS = new Set(["arb", "value"]);

const TAB_LABEL_BY_KEY = TABS.reduce((acc, tab) => {
  acc[tab.key] = tab.label;
  return acc;
}, {});

const MIN_THRESHOLD_LABEL_BY_TAB = {
  arb: "Lợi nhuận tối thiểu (%)",
  value: "EV tối thiểu (%)",
};

const SCOPE_OPTIONS = [
  { value: "both", label: "Tất cả", desc: "Nhận cả trước trận và trực tiếp." },
  { value: "live", label: "Trực tiếp", desc: "Chỉ nhận cơ hội live." },
  { value: "early", label: "Sớm", desc: "Chỉ nhận cơ hội trước trận." },
];

const BOOKMAKER_MODE_OPTIONS = [
  { value: TELEGRAM_BOOKMAKER_FILTER_MODES.UNLIMITED, label: "Không giới hạn" },
  { value: TELEGRAM_BOOKMAKER_FILTER_MODES.CUSTOM, label: "Tùy chọn" },
];

// ── Vietnamese error mapping (never renders raw error.message) ─────────────
const ERROR_MESSAGE_MAP = {
  firebase_auth_required: "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
  network_error: "Không thể kết nối máy chủ. Vui lòng thử lại.",
  telegram_bot_not_configured: "Bot Telegram chưa được cấu hình trên máy chủ.",
  telegram_not_linked: "Tài khoản Telegram chưa được liên kết.",
  tab_not_available: "Tính năng thông báo cho tab này chưa khả dụng.",
  invalid_request: "Cài đặt chưa hợp lệ. Vui lòng kiểm tra lại.",
  invalid_response: "Máy chủ trả về dữ liệu không hợp lệ.",
  internal_error: "Máy chủ đang gặp lỗi. Vui lòng thử lại sau.",
  account_check_failed:
    "Không thể xác minh tài khoản website. Vui lòng kiểm tra lại liên kết hoặc thử lại sau.",
};

const FALLBACK_ERROR_MESSAGE = "Không thể thực hiện lúc này. Vui lòng thử lại.";

const MIN_THRESHOLD_VALIDATION_MESSAGE =
  "Ngưỡng không hợp lệ. Vui lòng nhập số lớn hơn hoặc bằng 0, hoặc để trống.";

const BOOKMAKER_VALIDATION_MESSAGE =
  "Vui lòng chọn ít nhất một nhà cái hoặc chuyển sang “Không giới hạn”.";

function describeError(error) {
  const code = error instanceof TelegramNotificationSettingsApiError ? error.code : null;
  if (code && Object.prototype.hasOwnProperty.call(ERROR_MESSAGE_MAP, code)) {
    return ERROR_MESSAGE_MAP[code];
  }
  return FALLBACK_ERROR_MESSAGE;
}

function isTelegramNotLinkedError(error) {
  return error instanceof TelegramNotificationSettingsApiError && error.code === "telegram_not_linked";
}

// ── Small shared UI pieces (self-contained, no import from
//    AnalysisStatsPage.jsx) ──────────────────────────────────────────────────
function FieldLabel({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        color: C.dimmer,
        marginBottom: 4,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
      }}
    >
      {children}
    </label>
  );
}

function Note({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: C.dimmer,
        background: "rgba(76,111,255,0.06)",
        border: `1px solid ${C.accentSoft}`,
        borderRadius: 8,
        padding: "8px 10px",
        marginTop: 12,
        lineHeight: 1.55,
      }}
    >
      {children}
    </div>
  );
}

function SectionDivider() {
  return <div style={{ height: 1, background: C.divider, margin: "14px 0" }} />;
}

function ErrorBox({ children }) {
  return (
    <div
      role="alert"
      style={{
        fontSize: 12,
        color: C.negative,
        background: C.negativeBg,
        border: `1px solid ${C.negativeBorder}`,
        borderRadius: 8,
        padding: "9px 11px",
        marginTop: 10,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function StatusBox({ children }) {
  return (
    <div
      role="status"
      style={{
        fontSize: 12,
        color: C.positive,
        background: C.positiveBg,
        border: `1px solid ${C.positiveBorder}`,
        borderRadius: 8,
        padding: "9px 11px",
        marginTop: 10,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div
      role="status"
      style={{
        fontSize: 12,
        color: C.muted,
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${C.divider}`,
        borderRadius: 8,
        padding: "9px 11px",
        marginTop: 10,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "10px 14px",
        fontSize: 13,
        fontWeight: 700,
        background: disabled ? "rgba(76,111,255,0.35)" : C.accent,
        color: "#fff",
        border: "none",
        borderRadius: 9,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.75 : 1,
        boxSizing: "border-box",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "9px 14px",
        fontSize: 13,
        fontWeight: 700,
        background: "transparent",
        color: disabled ? C.dimmer : C.muted,
        border: `1px solid ${C.inputBorder}`,
        borderRadius: 9,
        cursor: disabled ? "default" : "pointer",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// Display-only Vietnamese labels for bookmaker group titles. Does not
// alter SPORTSBOOK_GROUPS / group.title itself; only used at render time
// with a safe fallback to the original title.
const BOOKMAKER_GROUP_TITLE_VI = Object.freeze({
  "MAJOR US BOOKS": "NHÀ CÁI LỚN TẠI MỸ",
  "SHARP BOOKS": "NHÀ CÁI CHUYÊN NGHIỆP",
  "REGIONAL & INTERNATIONAL": "NHÀ CÁI KHU VỰC & QUỐC TẾ",
  "EXCHANGES & PREDICTION MARKETS": "SÀN CƯỢC & THỊ TRƯỜNG DỰ ĐOÁN",
});

// ── Telegram Arb bookmaker filter section ───────────────────────────────────
//
// Renders the "BỘ LỌC NHÀ CÁI TELEGRAM" selector using ONLY the shared
// catalog imported at the top of this file (SPORTSBOOK_GROUPS /
// SELECTABLE_SPORTSBOOK_IDS / isSelectableSportsbookId). This
// component never declares its own bookmaker list, never hardcodes a
// label, and never renders a raw provisional id.
function BookmakerFilterSection({
  mode,
  selectedIds,
  validationMessage,
  disabled,
  onModeChange,
  onToggle,
  onSelectAll,
  onDeselectAll,
  onSelectGroup,
  onDeselectGroup,
}) {
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const totalSelectable = SELECTABLE_SPORTSBOOK_IDS.length;
  const isCustom = mode === TELEGRAM_BOOKMAKER_FILTER_MODES.CUSTOM;

  return (
    <div style={{ marginBottom: 4 }}>
      <FieldLabel>Bộ lọc nhà cái Telegram</FieldLabel>

      <div
        role="radiogroup"
        aria-label="Bộ lọc nhà cái Telegram"
        style={{ display: "flex", gap: 6, marginBottom: 8 }}
      >
        {BOOKMAKER_MODE_OPTIONS.map((opt) => {
          const selected = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled}
              disabled={disabled}
              onClick={() => onModeChange(opt.value)}
              style={{
                flex: 1,
                padding: "8px 4px",
                fontSize: 12,
                fontWeight: 700,
                background: selected ? C.accent : "transparent",
                color: disabled ? C.dimmer : selected ? "#fff" : C.muted,
                border: `1px solid ${selected ? C.accent : C.inputBorder}`,
                borderRadius: 8,
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.6 : 1,
                boxSizing: "border-box",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {!isCustom && (
        <div style={{ fontSize: 11, color: C.dimmer, lineHeight: 1.5, marginBottom: 8 }}>
          Nhận cơ hội từ mọi nhà cái phù hợp.
        </div>
      )}

      {isCustom && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
              Đã chọn {selectedIds.length} / {totalSelectable} nhà cái khả dụng
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={onSelectAll}
                disabled={disabled}
                aria-disabled={disabled}
                style={{
                  padding: "5px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: "transparent",
                  color: disabled ? C.dimmer : C.accent,
                  border: `1px solid ${C.inputBorder}`,
                  borderRadius: 6,
                  cursor: disabled ? "default" : "pointer",
                }}
              >
                Chọn tất cả
              </button>
              <button
                type="button"
                onClick={onDeselectAll}
                disabled={disabled}
                aria-disabled={disabled}
                style={{
                  padding: "5px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: "transparent",
                  color: disabled ? C.dimmer : C.muted,
                  border: `1px solid ${C.inputBorder}`,
                  borderRadius: 6,
                  cursor: disabled ? "default" : "pointer",
                }}
              >
                Bỏ chọn tất cả
              </button>
            </div>
          </div>

          {SPORTSBOOK_GROUPS.map((group) => {
            const activeItemsInGroup = group.items.filter((item) => isSelectableSportsbookId(item.id));
            const selectedInGroupCount = activeItemsInGroup.filter((item) => selectedIdSet.has(item.id)).length;
            return (
              <details
                key={group.key}
                style={{
                  marginBottom: 8,
                  border: `1px solid ${C.inputBorder}`,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <summary
                  style={{
                    cursor: disabled ? "default" : "pointer",
                    padding: "8px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.text,
                    background: "rgba(255,255,255,0.03)",
                    listStyle: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span>{BOOKMAKER_GROUP_TITLE_VI[group.title] || group.title}</span>
                  <span style={{ fontSize: 11, color: C.dimmer, fontWeight: 600 }}>
                    {selectedInGroupCount}/{activeItemsInGroup.length} đang hoạt động
                  </span>
                </summary>

                <div style={{ padding: "8px 10px" }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => onSelectGroup(group.key)}
                      disabled={disabled}
                      aria-disabled={disabled}
                      style={{
                        padding: "4px 9px",
                        fontSize: 10,
                        fontWeight: 700,
                        background: "transparent",
                        color: disabled ? C.dimmer : C.accent,
                        border: `1px solid ${C.inputBorder}`,
                        borderRadius: 6,
                        cursor: disabled ? "default" : "pointer",
                      }}
                    >
                      Chọn nhóm
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeselectGroup(group.key)}
                      disabled={disabled}
                      aria-disabled={disabled}
                      style={{
                        padding: "4px 9px",
                        fontSize: 10,
                        fontWeight: 700,
                        background: "transparent",
                        color: disabled ? C.dimmer : C.muted,
                        border: `1px solid ${C.inputBorder}`,
                        borderRadius: 6,
                        cursor: disabled ? "default" : "pointer",
                      }}
                    >
                      Bỏ nhóm
                    </button>
                  </div>

                  {group.items.map((item) => {
                    const selectable = isSelectableSportsbookId(item.id);
                    const checked = selectable && selectedIdSet.has(item.id);
                    const rowDisabled = disabled || !selectable;
                    return (
                      <label
                        key={item.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "5px 2px",
                          fontSize: 12,
                          color: !selectable ? C.dimmer : C.text,
                          opacity: !selectable ? 0.5 : 1,
                          cursor: rowDisabled ? "default" : "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={rowDisabled}
                          aria-disabled={rowDisabled}
                          onChange={() => onToggle(item.id)}
                        />
                        <span>{item.label}</span>
                        {!selectable && (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.dimmer,
                              background: "rgba(255,255,255,0.06)",
                              border: `1px solid ${C.inputBorder}`,
                              borderRadius: 999,
                              padding: "1px 6px",
                              marginLeft: "auto",
                            }}
                          >
                            Ngừng hoạt động
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </details>
            );
          })}
        </div>
      )}

      {validationMessage && <ErrorBox>{validationMessage}</ErrorBox>}

      <Note>
        Danh mục hiện là bản chuẩn bị trước khi có dữ liệu Sharp thật. Tên hiển thị đã được khóa; mã đối chiếu nhà cái sẽ được chuẩn hóa sau khi gia hạn API.
      </Note>
    </div>
  );
}

// ── Per-tab draft helpers (shared by Arb and Value; each call builds
//    brand-new objects/arrays so the Arb and Value drafts never share
//    a reference with each other or with the server response) ─────────────

function buildDefaultTabDraft() {
  return {
    enabled: false,
    scope: "both",
    minThresholdText: "",
    bookmakerMode: TELEGRAM_BOOKMAKER_FILTER_MODES.UNLIMITED,
    bookmakerIds: [],
  };
}

function buildTabDraftFromSettings(tabSettings) {
  const bookmakerDraft = buildTelegramBookmakerDraftFromSettings(
    tabSettings ? tabSettings.bookmakers : null
  );

  return {
    enabled: !!(tabSettings && tabSettings.enabled),
    scope: tabSettings && typeof tabSettings.scope === "string" ? tabSettings.scope : "both",
    minThresholdText:
      tabSettings && typeof tabSettings.minThreshold === "number"
        ? String(tabSettings.minThreshold)
        : "",
    bookmakerMode: bookmakerDraft.mode,
    bookmakerIds: bookmakerDraft.selectedIds,
  };
}

/**
 * Validates + converts the draft's minThresholdText into the wire
 * value ("" → null, else finite number >= 0). Returns
 * { ok: true, value } or { ok: false }.
 */
function parseMinThresholdDraft(text) {
  const trimmed = String(text == null ? "" : text).trim();
  if (trimmed === "") {
    return { ok: true, value: null };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false };
  }
  return { ok: true, value: n };
}

function draftEqualsServerSettings(draft, tabSettings) {
  if (!tabSettings) return false;
  if (draft.enabled !== !!tabSettings.enabled) return false;
  if (draft.scope !== tabSettings.scope) return false;
  const parsed = parseMinThresholdDraft(draft.minThresholdText);
  if (!parsed.ok) return false;
  const serverThreshold = typeof tabSettings.minThreshold === "number" ? tabSettings.minThreshold : null;
  if (parsed.value !== serverThreshold) return false;
  return telegramBookmakerDraftEqualsSettings(
    { mode: draft.bookmakerMode, selectedIds: draft.bookmakerIds },
    tabSettings.bookmakers
  );
}

function formatBookmakersMarketsNote(value) {
  if (value === null) {
    return "Không giới hạn";
  }
  if (Array.isArray(value)) {
    return null; // caller renders count separately
  }
  return "Không giới hạn";
}

export default function TelegramNotificationSettings({ apiClient }) {
  const client = apiClient || DEFAULT_API_CLIENT;

  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);

  // ── Load state ───────────────────────────────────────────────────────────
  const [loadPhase, setLoadPhase] = useState("loading"); // "loading" | "error" | "loaded"
  const [loadError, setLoadError] = useState(null);
  const [linked, setLinked] = useState(false);
  const [tabsData, setTabsData] = useState(null); // sanitized tabs object from server

  // ── Active tab ───────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("arb");

  // ── Link flow state ──────────────────────────────────────────────────────
  const [deepLink, setDeepLink] = useState(null);
  const [expiresInSec, setExpiresInSec] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const [checkingLink, setCheckingLink] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [checkNotice, setCheckNotice] = useState(null);

  // ── Per-tab draft/save state (Arb and Value are fully independent;
  //    each key holds its own object/array references, never shared) ───────
  const [draftsByTab, setDraftsByTab] = useState(() => ({
    arb: buildDefaultTabDraft(),
    value: buildDefaultTabDraft(),
  }));
  const [saveErrorByTab, setSaveErrorByTab] = useState({});
  const [saveNoticeByTab, setSaveNoticeByTab] = useState({});
  const [savingByTab, setSavingByTab] = useState({});
  const [validationErrorByTab, setValidationErrorByTab] = useState({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Load settings (with request-sequence guard against stale
  //    responses overwriting newer ones) ───────────────────────────────────
  const loadSettings = useCallback(() => {
    const seq = ++requestSeqRef.current;
    setLoadPhase("loading");
    setLoadError(null);

    client
      .getTelegramNotificationSettings()
      .then((result) => {
        if (!mountedRef.current || requestSeqRef.current !== seq) return;
        setLinked(!!(result && result.linked));
        const tabs = result && result.tabs ? result.tabs : null;
        setTabsData(tabs);
        if (tabs) {
          // Hydrate each writable tab's draft independently from its own
          // server payload. Never derive Value's draft from Arb's (or
          // vice versa) — each branch below only touches its own key.
          setDraftsByTab((prev) => {
            const next = { ...prev };
            if (tabs.arb) next.arb = buildTabDraftFromSettings(tabs.arb);
            if (tabs.value) next.value = buildTabDraftFromSettings(tabs.value);
            return next;
          });
          setValidationErrorByTab((prev) => {
            const next = { ...prev };
            if (tabs.arb) next.arb = null;
            if (tabs.value) next.value = null;
            return next;
          });
        }
        setLoadPhase("loaded");
      })
      .catch((error) => {
        if (!mountedRef.current || requestSeqRef.current !== seq) return;
        setLoadError(describeError(error));
        setLoadPhase("error");
      });
  }, [client]);

  useEffect(() => {
    loadSettings();
    // Intentionally run only once on mount; loadSettings identity is
    // stable per `client` reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Link Telegram ────────────────────────────────────────────────────────
  const handleStartLink = useCallback(() => {
    if (linking) return;
    setLinking(true);
    setLinkError(null);

    client
      .startTelegramLink()
      .then((result) => {
        if (!mountedRef.current) return;
        setLinking(false);

        if (result && result.status === "already_linked") {
          setDeepLink(null);
          setExpiresInSec(null);
          loadSettings();
          return;
        }

        if (result && result.status === "created") {
          setDeepLink(result.deepLink);
          setExpiresInSec(result.expiresInSec);

          if (typeof window !== "undefined") {
            window.open(result.deepLink, "_blank", "noopener,noreferrer");
          }
        }
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        setLinking(false);
        setLinkError(describeError(error));
      });
  }, [client, linking, loadSettings]);

  // ── Re-check link status ─────────────────────────────────────────────────
  const handleCheckLink = useCallback(() => {
    if (checkingLink) return;
    setCheckingLink(true);
    setCheckError(null);
    setCheckNotice(null);

    client
      .getTelegramLinkStatus()
      .then((result) => {
        if (!mountedRef.current) return;
        setCheckingLink(false);

        if (result && result.status === "already_linked") {
          setDeepLink(null);
          setExpiresInSec(null);
          loadSettings();
          return;
        }

        setLinked(false);
        setTabsData(null);
        setDeepLink(null);
        setExpiresInSec(null);
        setSaveNoticeByTab({});
        setSaveErrorByTab({});
        setCheckNotice("Chưa tìm thấy liên kết Telegram. Vui lòng hoàn tất liên kết trong Telegram rồi thử lại.");
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        setCheckingLink(false);
        setCheckError(describeError(error));
      });
  }, [client, checkingLink, loadSettings]);

  // ── Active-tab derived settings/draft ────────────────────────────────────
  const activeTabSettings = tabsData ? tabsData[activeTab] : null;
  const activeTabAvailable = !!(activeTabSettings && activeTabSettings.available === true);
  const isWritableTab = WRITABLE_UI_TABS.has(activeTab);
  const activeDraft = isWritableTab ? draftsByTab[activeTab] : null;
  const activeSaving = !!savingByTab[activeTab];
  const activeSaveError = saveErrorByTab[activeTab] || null;
  const activeSaveNotice = saveNoticeByTab[activeTab] || null;
  const activeValidationError = validationErrorByTab[activeTab] || null;

  // ── Draft mutation helpers (always mutate ONLY draftsByTab[activeTab];
  //    Arb and Value never touch each other's entry) ────────────────────────
  const handleToggleEnabled = useCallback((nextEnabled) => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], enabled: nextEnabled } }));
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleScopeChange = useCallback((nextScope) => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], scope: nextScope } }));
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleThresholdChange = useCallback((nextText) => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], minThresholdText: nextText } }));
    const parsed = parseMinThresholdDraft(nextText);
    setValidationErrorByTab((prev) => ({ ...prev, [tab]: parsed.ok ? null : MIN_THRESHOLD_VALIDATION_MESSAGE }));
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleBookmakerModeChange = useCallback((nextMode) => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    if (
      nextMode !== TELEGRAM_BOOKMAKER_FILTER_MODES.UNLIMITED &&
      nextMode !== TELEGRAM_BOOKMAKER_FILTER_MODES.CUSTOM
    ) {
      return;
    }
    const tab = activeTab;
    setDraftsByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], bookmakerMode: nextMode } }));
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleBookmakerToggle = useCallback((id) => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    if (!isSelectableSportsbookId(id)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => {
      const nextSet = new Set(prev[tab].bookmakerIds);
      if (nextSet.has(id)) {
        nextSet.delete(id);
      } else {
        nextSet.add(id);
      }
      return {
        ...prev,
        [tab]: { ...prev[tab], bookmakerIds: normalizeTelegramBookmakerIds(Array.from(nextSet)) },
      };
    });
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleBookmakerSelectAll = useCallback(() => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => ({
      ...prev,
      [tab]: { ...prev[tab], bookmakerIds: normalizeTelegramBookmakerIds(SELECTABLE_SPORTSBOOK_IDS) },
    }));
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleBookmakerDeselectAll = useCallback(() => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], bookmakerIds: [] } }));
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleBookmakerSelectGroup = useCallback((groupKey) => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => {
      const group = SPORTSBOOK_GROUPS.find((g) => g.key === groupKey);
      if (!group) return prev;
      const activeIdsInGroup = group.items
        .filter((item) => isSelectableSportsbookId(item.id))
        .map((item) => item.id);
      const merged = prev[tab].bookmakerIds.concat(activeIdsInGroup);
      return { ...prev, [tab]: { ...prev[tab], bookmakerIds: normalizeTelegramBookmakerIds(merged) } };
    });
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const handleBookmakerDeselectGroup = useCallback((groupKey) => {
    if (!WRITABLE_UI_TABS.has(activeTab)) return;
    const tab = activeTab;
    setDraftsByTab((prev) => {
      const group = SPORTSBOOK_GROUPS.find((g) => g.key === groupKey);
      if (!group) return prev;
      const idsInGroup = new Set(group.items.map((item) => item.id));
      const filtered = prev[tab].bookmakerIds.filter((id) => !idsInGroup.has(id));
      return { ...prev, [tab]: { ...prev[tab], bookmakerIds: normalizeTelegramBookmakerIds(filtered) } };
    });
    setSaveNoticeByTab((prev) => ({ ...prev, [tab]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tab]: null }));
  }, [activeTab]);

  const bookmakerValidation = useMemo(() => {
    if (!activeDraft) return { ok: true, value: null };
    return validateTelegramBookmakerDraft({
      mode: activeDraft.bookmakerMode,
      selectedIds: activeDraft.bookmakerIds,
    });
  }, [activeDraft]);

  const bookmakerValidationMessage = bookmakerValidation.ok ? null : BOOKMAKER_VALIDATION_MESSAGE;

  const isDirty = useMemo(() => {
    if (!isWritableTab || !activeDraft || !activeTabSettings) return false;
    return !draftEqualsServerSettings(activeDraft, activeTabSettings);
  }, [isWritableTab, activeDraft, activeTabSettings]);

  // ── Save (captures the tab being saved at call-time, so a later tab
  //    switch or a concurrent edit to the OTHER tab's draft can never
  //    change which tab this in-flight request reads from or writes
  //    back to) ─────────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const tabToSave = activeTab;
    if (!WRITABLE_UI_TABS.has(tabToSave)) return;
    if (savingByTab[tabToSave]) return;

    const tabSettingsAtStart = tabsData ? tabsData[tabToSave] : null;
    const tabAvailableAtStart = !!(tabSettingsAtStart && tabSettingsAtStart.available === true);
    if (!linked || !tabAvailableAtStart || loadPhase !== "loaded") return;

    const draftToSave = draftsByTab[tabToSave];
    if (!draftToSave) return;

    const parsedThreshold = parseMinThresholdDraft(draftToSave.minThresholdText);
    if (!parsedThreshold.ok) {
      setValidationErrorByTab((prev) => ({ ...prev, [tabToSave]: MIN_THRESHOLD_VALIDATION_MESSAGE }));
      return;
    }

    const bookmakerValidationForSave = validateTelegramBookmakerDraft({
      mode: draftToSave.bookmakerMode,
      selectedIds: draftToSave.bookmakerIds,
    });
    if (!bookmakerValidationForSave.ok) {
      // Message is already shown live by BookmakerFilterSection; do not
      // call the API and do not enter the saving state.
      return;
    }

    if (draftEqualsServerSettings(draftToSave, tabSettingsAtStart)) return;

    setValidationErrorByTab((prev) => ({ ...prev, [tabToSave]: null }));
    setSaveErrorByTab((prev) => ({ ...prev, [tabToSave]: null }));
    setSaveNoticeByTab((prev) => ({ ...prev, [tabToSave]: null }));
    setSavingByTab((prev) => ({ ...prev, [tabToSave]: true }));

    client
      .updateTelegramNotificationSettings({
        tab: tabToSave,
        enabled: draftToSave.enabled,
        scope: draftToSave.scope,
        minThreshold: parsedThreshold.value,
        bookmakers: bookmakerValidationForSave.value,
      })
      .then((result) => {
        if (!mountedRef.current) return;
        setSavingByTab((prev) => ({ ...prev, [tabToSave]: false }));

        // Defensive: only ever apply a response back onto the tab it was
        // requested for. A malformed/mismatched response never touches
        // the other tab's settings/draft/dirty/error/notice state.
        if (!result || result.tab !== tabToSave || !result.settings) {
          setSaveErrorByTab((prev) => ({ ...prev, [tabToSave]: FALLBACK_ERROR_MESSAGE }));
          return;
        }

        const nextTabSettings = result.settings;
        setTabsData((prev) => {
          const base = prev || {};
          return { ...base, [tabToSave]: nextTabSettings };
        });
        setDraftsByTab((prev) => ({ ...prev, [tabToSave]: buildTabDraftFromSettings(nextTabSettings) }));
        setSaveNoticeByTab((prev) => ({
          ...prev,
          [tabToSave]: `Đã lưu cài đặt thông báo ${TAB_LABEL_BY_KEY[tabToSave] || ""}.`,
        }));
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        setSavingByTab((prev) => ({ ...prev, [tabToSave]: false }));

        if (isTelegramNotLinkedError(error)) {
          setLinked(false);
          setTabsData(null);
          setDeepLink(null);
          setExpiresInSec(null);
          setSaveErrorByTab({});
          setSaveNoticeByTab({});
          setCheckNotice("Tài khoản Telegram chưa được liên kết. Vui lòng liên kết lại để tiếp tục.");
          return;
        }

        setSaveErrorByTab((prev) => ({ ...prev, [tabToSave]: describeError(error) }));
      });
  }, [client, activeTab, savingByTab, tabsData, linked, loadPhase, draftsByTab]);

  const thresholdDraftValid = useMemo(() => {
    if (!activeDraft) return true;
    return parseMinThresholdDraft(activeDraft.minThresholdText).ok;
  }, [activeDraft]);

  const saveDisabled =
    activeSaving ||
    loadPhase !== "loaded" ||
    !linked ||
    !activeTabAvailable ||
    !thresholdDraftValid ||
    !bookmakerValidation.ok ||
    !isDirty;

  const controlsDisabled = !activeTabAvailable || activeSaving;
  const isAnyTabSaving = Object.keys(savingByTab).some((key) => !!savingByTab[key]);

  // ── Render: loading ──────────────────────────────────────────────────────
  if (loadPhase === "loading") {
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <div style={{ fontSize: 13, color: C.muted, padding: "18px 4px", textAlign: "center" }}>
          Đang tải cài đặt thông báo Telegram…
        </div>
      </div>
    );
  }

  // ── Render: load error ───────────────────────────────────────────────────
  if (loadPhase === "error") {
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <ErrorBox>{loadError || FALLBACK_ERROR_MESSAGE}</ErrorBox>
        <div style={{ marginTop: 12 }}>
          <SecondaryButton onClick={loadSettings}>Thử lại</SecondaryButton>
        </div>
      </div>
    );
  }

  // ── Render: loaded ───────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", boxSizing: "border-box" }}>
      {!linked && (
        <div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.negative,
              background: C.negativeBg,
              border: `1px solid ${C.negativeBorder}`,
              borderRadius: 8,
              padding: "9px 11px",
              marginBottom: 12,
            }}
          >
            Telegram chưa được liên kết
          </div>

          <PrimaryButton onClick={handleStartLink} disabled={linking}>
            {linking ? "Đang tạo liên kết…" : "Liên kết Telegram"}
          </PrimaryButton>

          {linkError && <ErrorBox>{linkError}</ErrorBox>}

          {deepLink && (
            <div style={{ marginTop: 10 }}>
              <InfoBox>
                Nếu Telegram không tự mở, hãy dùng nút bên dưới.
                {typeof expiresInSec === "number" && expiresInSec > 0 && (
                  <div style={{ marginTop: 4 }}>
                    Liên kết có hiệu lực khoảng {Math.max(1, Math.round(expiresInSec / 60))} phút.
                  </div>
                )}
              </InfoBox>
              <a
                href={deepLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  marginTop: 8,
                  padding: "9px 14px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: C.accent,
                  background: C.accentSoft,
                  border: `1px solid ${C.accent}`,
                  borderRadius: 9,
                  textDecoration: "none",
                  boxSizing: "border-box",
                }}
              >
                Mở bot Telegram
              </a>
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <SecondaryButton onClick={handleCheckLink} disabled={checkingLink}>
              {checkingLink ? "Đang kiểm tra…" : "Kiểm tra lại liên kết"}
            </SecondaryButton>
          </div>

          {checkError && <ErrorBox>{checkError}</ErrorBox>}
          {checkNotice && <InfoBox>{checkNotice}</InfoBox>}
        </div>
      )}

      {linked && (
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: 12,
              fontWeight: 700,
              color: C.positive,
              background: C.positiveBg,
              border: `1px solid ${C.positiveBorder}`,
              borderRadius: 20,
              padding: "4px 12px",
              marginBottom: 14,
            }}
          >
            Telegram đã liên kết
          </div>

          <div style={{ marginBottom: 8 }}>
            <SecondaryButton onClick={handleCheckLink} disabled={checkingLink || isAnyTabSaving}>
              {checkingLink ? "Đang kiểm tra…" : "Kiểm tra lại liên kết"}
            </SecondaryButton>
          </div>
          {checkError && <ErrorBox>{checkError}</ErrorBox>}
          {checkNotice && <InfoBox>{checkNotice}</InfoBox>}

          <SectionDivider />

          {/* Tabs */}
          <div role="tablist" style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {TABS.map((tab) => {
              const tabSettings = tabsData ? tabsData[tab.key] : null;
              const isWritable = WRITABLE_UI_TABS.has(tab.key);
              const available =
                isWritable &&
                !!(tabSettings && tabSettings.available === true);
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-disabled={!available}
                  disabled={!available}
                  onClick={() => {
                    if (available) setActiveTab(tab.key);
                  }}
                  style={{
                    flex: "1 1 76px",
                    minWidth: 76,
                    padding: "8px 4px",
                    fontSize: 11,
                    fontWeight: 700,
                    background: isActive && available ? C.accent : "transparent",
                    color: !available ? C.dimmer : isActive ? "#fff" : C.muted,
                    border: `1px solid ${isActive && available ? C.accent : C.inputBorder}`,
                    borderRadius: 8,
                    cursor: available ? "pointer" : "default",
                    opacity: available ? 1 : 0.6,
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ fontSize: 15, marginBottom: 2 }}>{tab.icon}</div>
                  <div>{tab.label}</div>
                  {!available && (
                    <div style={{ fontSize: 9, marginTop: 2, color: C.dimmer }}>Sắp ra mắt</div>
                  )}
                </button>
              );
            })}
          </div>

          {!isWritableTab && (
            <Note>Tính năng thông báo cho tab này chưa khả dụng. Vui lòng quay lại sau.</Note>
          )}

          {isWritableTab && !activeTabAvailable && (
            <Note>Tính năng thông báo cho tab này chưa khả dụng.</Note>
          )}

          {isWritableTab && activeTabAvailable && activeTabSettings && activeDraft && (
            <div>
              {/* Enable toggle */}
              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor={`telegram-${activeTab}-enabled`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    cursor: controlsDisabled ? "default" : "pointer",
                    opacity: controlsDisabled ? 0.75 : 1,
                  }}
                >
                  <input
                    id={`telegram-${activeTab}-enabled`}
                    type="checkbox"
                    checked={activeDraft.enabled}
                    disabled={controlsDisabled}
                    onChange={(e) => handleToggleEnabled(e.target.checked)}
                  />
                  Nhận thông báo Telegram
                </label>
              </div>

              {/* Scope */}
              <div style={{ marginBottom: 14 }}>
                <FieldLabel>Phạm vi nhận thông báo</FieldLabel>
                <div role="radiogroup" aria-label="Phạm vi nhận thông báo" style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  {SCOPE_OPTIONS.map((opt) => {
                    const selected = activeDraft.scope === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={controlsDisabled}
                        onClick={() => handleScopeChange(opt.value)}
                        style={{
                          flex: 1,
                          padding: "8px 4px",
                          fontSize: 12,
                          fontWeight: 700,
                          background: selected ? C.accent : "transparent",
                          color: controlsDisabled ? C.dimmer : selected ? "#fff" : C.muted,
                          border: `1px solid ${selected ? C.accent : C.inputBorder}`,
                          borderRadius: 8,
                          cursor: controlsDisabled ? "default" : "pointer",
                          opacity: controlsDisabled ? 0.6 : 1,
                          boxSizing: "border-box",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: C.dimmer, lineHeight: 1.5 }}>
                  {SCOPE_OPTIONS.find((o) => o.value === activeDraft.scope)?.desc}
                </div>
              </div>

              {/* Min threshold */}
              <div style={{ marginBottom: 8 }}>
                <FieldLabel htmlFor={`telegram-${activeTab}-min-threshold`}>
                  {MIN_THRESHOLD_LABEL_BY_TAB[activeTab]}
                </FieldLabel>
                <input
                  id={`telegram-${activeTab}-min-threshold`}
                  type="number"
                  min="0"
                  step="0.1"
                  value={activeDraft.minThresholdText}
                  disabled={controlsDisabled}
                  onChange={(e) => handleThresholdChange(e.target.value)}
                  style={{
                    width: "100%",
                    background: C.inputBg,
                    border: `1px solid ${C.inputBorder}`,
                    borderRadius: 8,
                    padding: "9px 12px",
                    color: C.text,
                    fontSize: 14,
                    outline: "none",
                    boxSizing: "border-box",
                    cursor: controlsDisabled ? "default" : "text",
                    opacity: controlsDisabled ? 0.75 : 1,
                  }}
                />
                <div style={{ fontSize: 11, color: C.dimmer, marginTop: 4, lineHeight: 1.5 }}>
                  Để trống nghĩa là không giới hạn ngưỡng.
                </div>
              </div>

              {activeValidationError && <ErrorBox>{activeValidationError}</ErrorBox>}

              <SectionDivider />

              {/* BỘ LỌC NHÀ CÁI TELEGRAM */}
              <BookmakerFilterSection
                mode={activeDraft.bookmakerMode}
                selectedIds={activeDraft.bookmakerIds}
                validationMessage={bookmakerValidationMessage}
                disabled={controlsDisabled}
                onModeChange={handleBookmakerModeChange}
                onToggle={handleBookmakerToggle}
                onSelectAll={handleBookmakerSelectAll}
                onDeselectAll={handleBookmakerDeselectAll}
                onSelectGroup={handleBookmakerSelectGroup}
                onDeselectGroup={handleBookmakerDeselectGroup}
              />

              <SectionDivider />

              {/* Markets filter (read-only in this phase; no market selector implemented) */}
              <div style={{ marginBottom: 4 }}>
                <FieldLabel>Bộ lọc thị trường</FieldLabel>
                {Array.isArray(activeTabSettings.markets) ? (
                  <InfoBox>Đang có bộ lọc {activeTabSettings.markets.length} thị trường được lưu</InfoBox>
                ) : (
                  <InfoBox>Bộ lọc thị trường: {formatBookmakersMarketsNote(activeTabSettings.markets)}</InfoBox>
                )}
              </div>

              <Note>
                Cài đặt nhà cái hiển thị trên UI và nhà cái nhận thông báo Telegram là hai bộ lọc độc lập.
              </Note>

              <div style={{ marginTop: 14 }}>
                <PrimaryButton onClick={handleSave} disabled={saveDisabled}>
                  {activeSaving ? "Đang lưu…" : "Lưu cài đặt"}
                </PrimaryButton>
              </div>

              {activeSaveError && <ErrorBox>{activeSaveError}</ErrorBox>}
              {activeSaveNotice && <StatusBox>{activeSaveNotice}</StatusBox>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
